import React from 'react';
import type { ResolvedEpicConfig } from '../../../../../../shared/types/epics';
import { useAuth } from '../../../../lib/auth/AuthContext';
import { formatDate } from '../format';

// FORGE-207: "Config this run used" — the plan/stack/limits the agents actually
// ran with, snapshotted at boot. Makes a stack/plan mismatch (e.g. a
// dotnet/business project that ran as node/starter — the C#-as-node incident)
// visible at a glance instead of requiring a DDB dive.
export const ResolvedConfigCard: React.FC<{ config: ResolvedEpicConfig }> = ({ config }) => {
  // The plan/stack here is a SNAPSHOT of what this run used, frozen at boot —
  // not the live setting. If the workspace plan has since changed (e.g. the
  // project was linked to a paid account after an early orphaned run), the
  // snapshot legitimately disagrees with the current plan. Surface the live
  // workspace plan so a stale snapshot isn't misread as a misconfiguration.
  const { currentAccount } = useAuth();
  const livePlan = currentAccount?.plan ?? null;
  const planDiffers =
    !!livePlan &&
    !!config.plan &&
    livePlan.toLowerCase() !== config.plan.toLowerCase();

  const stacks = [
    ['backend', config.stackBackend],
    ['web', config.stackWeb],
    ['mobile', config.stackMobile],
    ['windows', config.stackWindows],
    ['win-service', config.stackWindowsService],
    ['database', config.stackDatabase],
  ].filter(([, v]) => v && v !== 'none') as [string, string][];

  const cell = (label: string, value: React.ReactNode) => (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 font-mono text-sm text-slate-900">{value}</div>
    </div>
  );

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-sm font-semibold text-slate-900">Config this run used</span>
        {config.snapshotBy && (
          <span className="text-xs text-slate-500">
            snapshot from {config.snapshotBy}
            {config.snapshotAt ? ` · ${formatDate(config.snapshotAt)}` : ''}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {cell('Plan', config.plan ?? <span className="text-slate-400">—</span>)}
        {cell(
          'Stack',
          stacks.length
            ? stacks.map(([k, v]) => `${k}:${v}`).join('  ')
            : <span className="text-slate-400">none</span>,
        )}
        {cell('Max iterations', config.maxIterations ?? <span className="text-slate-400">—</span>)}
        {cell(
          'Repo skills',
          config.repoSkillsEnabled == null ? (
            <span className="text-slate-400">—</span>
          ) : config.repoSkillsEnabled ? (
            'on'
          ) : (
            'off'
          ),
        )}
      </div>
      {config.mcpServerCount != null && config.mcpServerCount > 0 && (
        <div className="mt-1.5 text-xs text-slate-500">
          {config.mcpServerCount} MCP server{config.mcpServerCount === 1 ? '' : 's'} attached
        </div>
      )}
      {planDiffers && (
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
          This is a snapshot from an earlier run. The current workspace plan is{' '}
          <span className="font-semibold capitalize">{livePlan}</span> — a new run will use it.
        </div>
      )}
    </div>
  );
};
